import React, { useState, useEffect } from "react";
import { TextField, Tabs, Tab, Box } from "@mui/material";
import "../styles/profile.scss";
import GlobalLoader from "../components/GlobalLoader";
import ChangePasswordPopup from "../components/ChangePasswordPopup";
import Preference from "../components/Preference";
import Gamification from "../components/Gamification";
import {
  getUserProfile,
  updateUserProfile,
  getUserBadges,
  changeUserPassword,
} from "../helper/apicalls/user";
import SuccessPopup from "../components/common/SuccessPopup";

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
};

const Profile = () => {
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    grade: "",
    batch_name: "",
    course_name: "",
    institution_name: "",
    intitution_type: "",
    register_no: "",
    id: "",
    ai_teaching_preference: "",
    test_page_preference: "",
  });

  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({ ...profileData });
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [userStats, setUserStats] = useState(null);
  const [userBadges, setUserBadges] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("success");

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const [profileResponse, badgesResponse] = await Promise.all([
          getUserProfile(),
          getUserBadges(),
        ]);

        const userData = profileResponse?.data || profileResponse;
        const badgesData = badgesResponse?.data || badgesResponse;

        setPreferences(userData?.preference || []);
        setUserStats(badgesData);
        setUserBadges(badgesData);

        setProfileData({
          name: userData?.name || "",
          email: userData?.email || "",
          grade: userData?.grade || "",
          batch_name: userData?.batch_name || "",
          course_name: userData?.course_name || "",
          institution_name: userData?.institution_name || "",
          intitution_type: userData?.intitution_type || "",
          register_no: userData?.register_no || "",
          id: userData?.id || "",
          ai_teaching_preference: userData?.ai_teaching_preference || "easy",
          test_page_preference: userData?.test_page_preference || "easy",
        });
        setEditedData({
          name: userData?.name || "",
          email: userData?.email || "",
          grade: userData?.grade || "",
          batch_name: userData?.batch_name || "",
          course_name: userData?.course_name || "",
          institution_name: userData?.institution_name || "",
          intitution_type: userData?.intitution_type || "",
          register_no: userData?.register_no || "",
          id: userData?.id || "",
          ai_teaching_preference: userData?.ai_teaching_preference || "easy",
          test_page_preference: userData?.test_page_preference || "easy",
        });
      } catch (err) {
        console.error("Error fetching user profile:", err);
        setError("Failed to load profile data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const handleEditToggle = () => {
    if (isEditing) {
      setEditedData({ ...profileData });
    } else {
      setEditedData({ ...profileData });
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (field, value) => {
    setEditedData({
      ...editedData,
      [field]: value,
    });
  };

  const handleSave = async () => {
    try {
      setUpdateLoading(true);
      setUpdateError(null);

      const updateData = {
        name: editedData.name,
        grade: editedData.grade,
      };

      const response = await updateUserProfile(updateData);

      const refreshResponse = await getUserProfile();
      const refreshedUserData = refreshResponse?.data || refreshResponse;

      setProfileData({
        name: refreshedUserData?.name || "",
        email: refreshedUserData?.email || "",
        grade: refreshedUserData?.grade || "",
        batch_name: refreshedUserData?.batch_name || "",
        course_name: refreshedUserData?.course_name || "",
        institution_name: refreshedUserData?.institution_name || "",
        intitution_type: refreshedUserData?.intitution_type || "",
        register_no: refreshedUserData?.register_no || "",
        id: refreshedUserData?.id || "",
        ai_teaching_preference:
          refreshedUserData?.ai_teaching_preference || "easy",
        test_page_preference: refreshedUserData?.test_page_preference || "easy",
      });

      setPreferences(refreshedUserData?.preference || []);

      setEditedData({
        name: refreshedUserData?.name || "",
        email: refreshedUserData?.email || "",
        grade: refreshedUserData?.grade || "",
        batch_name: refreshedUserData?.batch_name || "",
        course_name: refreshedUserData?.course_name || "",
        institution_name: refreshedUserData?.institution_name || "",
        intitution_type: refreshedUserData?.intitution_type || "",
        register_no: refreshedUserData?.register_no || "",
        id: refreshedUserData?.id || "",
        ai_teaching_preference:
          refreshedUserData?.ai_teaching_preference || "easy",
        test_page_preference: refreshedUserData?.test_page_preference || "easy",
      });

      setIsEditing(false);
    } catch (err) {
      console.error("Error updating profile:", err);
      setUpdateError("Failed to update profile. Please try again.");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleChangePassword = async (passwordData) => {
    try {
      setUpdateLoading(true);
      const payload = {
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
      };
      await changeUserPassword(payload);
      setPopupMessage("Password changed successfully!");
      setPopupType("success");
      setShowSuccessPopup(true);
    } catch (err) {
      console.error("Error changing password:", err);
      const errMsg = err.response?.data?.message || err.response?.data?.error || "Failed to change password. Please try again.";
      setPopupMessage(errMsg);
      setPopupType("error");
      setShowSuccessPopup(true);
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <div className="profile-dashboard">
      <div className="profile-content">
        <div className="profile-card">
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="profile tabs"
            >
              <Tab label="Profile" />
              <Tab label="Preferences" />
              <Tab label="Stats" />
              <Tab label="Badges" />
            </Tabs>
          </Box>

          {loading ? (
            <GlobalLoader />
          ) : error ? (
            <div className="error-container">
              <div className="error-message">{error}</div>
              <button
                className="retry-button"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              <TabPanel
                className="tab-data custom-scrollbar"
                value={activeTab}
                index={0}
              >
                <div className="section-header">
                  <div className="avatar-section">
                    <div className="avatar-container">
                      <div className="avatar-letter">
                        {(profileData.name || "U").charAt(0).toUpperCase()}
                      </div>
                      {/* <button className="camera-button">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 12.3198V17.25C21 17.8023 20.5523 18.25 20 18.25H4C3.44772 18.25 3 17.8023 3 17.25V6.75C3 6.19772 3.44772 5.75 4 5.75H12.5607"
                            stroke="#4169FF"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <circle
                            cx="15.5"
                            cy="10"
                            r="3"
                            stroke="#4169FF"
                            strokeWidth="1.5"
                          />
                          <circle
                            cx="17"
                            cy="8.5"
                            r="1.5"
                            stroke="white"
                            strokeWidth="1.5"
                          />
                        </svg>
                      </button> */}
                    </div>
                  </div>
                  <button className="edit-link" onClick={handleEditToggle}>
                    {isEditing ? "Done" : "Edit"}
                  </button>
                </div>

                <div className="profile-form">
                  <div className="form-row">
                    <div className="form-group">
                      <TextField
                        label="Name"
                        variant="outlined"
                        fullWidth
                        value={isEditing ? editedData.name : profileData.name}
                        onChange={(e) =>
                          handleInputChange("name", e.target.value)
                        }
                        disabled={!isEditing}
                        InputProps={{
                          className: `custom-input ${
                            isEditing ? "editable" : "disabled-field"
                          }`,
                        }}
                      />
                    </div>

                    <div className="form-group">
                      <TextField
                        label="Email"
                        variant="outlined"
                        fullWidth
                        value={profileData.email}
                        disabled={true}
                        InputProps={{
                          className: "custom-input disabled-field",
                        }}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <TextField
                        label="Grade"
                        variant="outlined"
                        fullWidth
                        value={profileData.grade}
                        disabled={true}
                        InputProps={{
                          className: "custom-input disabled-field",
                        }}
                      />
                    </div>

                    <div className="form-group">
                      <TextField
                        label="Batch Name"
                        variant="outlined"
                        fullWidth
                        value={profileData.batch_name}
                        disabled={true}
                        InputProps={{
                          className: "custom-input disabled-field",
                        }}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <TextField
                        label="Course Name"
                        variant="outlined"
                        fullWidth
                        value={profileData.course_name}
                        disabled={true}
                        InputProps={{
                          className: "custom-input disabled-field",
                        }}
                      />
                    </div>

                    <div className="form-group">
                      <TextField
                        label="Institution Name"
                        variant="outlined"
                        fullWidth
                        value={profileData.institution_name}
                        disabled={true}
                        InputProps={{
                          className: "custom-input disabled-field",
                        }}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <TextField
                        label="Institution Type"
                        variant="outlined"
                        fullWidth
                        value={profileData.intitution_type}
                        disabled={true}
                        InputProps={{
                          className: "custom-input disabled-field",
                        }}
                      />
                    </div>
                  </div>

                  {isEditing && (
                    <div className="form-actionss">
                      <button
                        className="save-button"
                        onClick={handleSave}
                        disabled={updateLoading}
                      >
                        {updateLoading ? "Saving..." : "Save"}
                      </button>
                      {updateError && (
                        <div className="update-error">{updateError}</div>
                      )}
                    </div>
                  )}
                </div>

                <div className="change-password-section">
                  <button
                    className="change-password-button"
                    onClick={() => setShowPasswordPopup(true)}
                  >
                    Change Password
                  </button>
                </div>
              </TabPanel>

              <TabPanel
                className="tab-data custom-scrollbar"
                value={activeTab}
                index={1}
              >
                <Preference
                  profileData={profileData}
                  setPreferences={setPreferences}
                  preferences={preferences}
                  onUpdateProfile={async (updatedData) => {
                    const response = await updateUserProfile(updatedData);
                    setProfileData(updatedData);
                    return response;
                  }}
                />
              </TabPanel>

              <TabPanel
                className="tab-data custom-scrollbar gamification-tab"
                value={activeTab}
                index={2}
              >
                <Gamification stats={userStats} type="stats" />
              </TabPanel>

              <TabPanel
                className="tab-data custom-scrollbar batches-tab"
                value={activeTab}
                index={3}
              >
                <Gamification badges={userBadges} type="badges" />
              </TabPanel>
            </>
          )}
        </div>
      </div>

      {showPasswordPopup && (
        <ChangePasswordPopup
          isOpen={showPasswordPopup}
          onClose={() => setShowPasswordPopup(false)}
          onChangePassword={handleChangePassword}
        />
      )}

      <SuccessPopup
        open={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        message={popupMessage}
        type={popupType}
      />
    </div>
  );
};

export default Profile;
